import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { filterLocalizedTimeZones, localizedTimeZoneOptions } from '../utils/timezones';
import { useI18n } from '../i18n/I18nProvider';

export function TimeZoneCombobox({ value, onChange, label }: { value: string; onChange: (value: string) => void; label: string }) {
  const { locale, t } = useI18n();
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const options = useMemo(() => localizedTimeZoneOptions(locale), [locale]);
  const visible = useMemo(() => filterLocalizedTimeZones(options, query, value, locale).slice(0, 120), [locale, options, query, value]);
  const selected = options.find((option) => option.value === value);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    requestAnimationFrame(() => search.current?.focus());
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return <div className="timezone-combobox" ref={root} onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}>
    <span className="timezone-combobox__label">{label}</span>
    <button type="button" className="timezone-combobox__trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
      <span><strong>{selected?.label || value}</strong><small>{value}</small></span><ChevronDown />
    </button>
    {open && <div className="timezone-combobox__popover">
      <label className="timezone-combobox__search"><Search /><span className="visually-hidden">{t('admin.timezoneSearch')}</span><input ref={search} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('admin.timezoneSearchPlaceholder')} /></label>
      <div className="timezone-combobox__options" role="listbox" aria-label={label}>{visible.map((option) => <button type="button" role="option" aria-selected={option.value === value} key={option.value} onClick={() => { onChange(option.value); setQuery(''); setOpen(false); }}>
        <span><strong>{option.label}</strong><small>{option.value}</small></span>{option.value === value && <Check />}
      </button>)}</div>
      {!visible.length && <p className="timezone-combobox__empty">{t('admin.timezoneMatches', { count: 0 })}</p>}
    </div>}
  </div>;
}
